"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";

import { cn } from "../lib/cn";

const badgeVariants = cva(
  "inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border border-transparent px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-3",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary:
          "bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive: "bg-destructive text-white [a&]:hover:bg-destructive/90",
        success: "bg-success text-success-foreground [a&]:hover:bg-success/90",
        warning: "bg-warning text-warning-foreground [a&]:hover:bg-warning/90",
        info: "bg-info text-info-foreground [a&]:hover:bg-info/90",
        outline:
          "border-border text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        // Toned outlines: the signal in the text and the hairline over a light
        // wash, rather than a solid fill. Call sites were already building this
        // by hand on top of `outline` (sales-feedback-panel, competitor-map).
        //
        // 28% border over a 10% fill is Qcom's --*-line / --*-dim pair, which
        // ~94 status chips already read against. The hand-built qtrack pair
        // used 40%; matching the larger population keeps far more of the UI
        // unchanged, and 40 -> 28 is barely perceptible on the two that move.
        "outline-success": "border-success/28 bg-success/10 text-success",
        "outline-warning": "border-warning/28 bg-warning/10 text-warning",
        "outline-destructive": "border-destructive/28 bg-destructive/10 text-destructive",
        "outline-info": "border-info/26 bg-info/10 text-info",
        "outline-primary": "border-primary/28 bg-primary/10 text-primary",
        // Categorical, not a severity: for chips that name a kind of thing
        // (a flow, a class of run) where the hue distinguishes rather than
        // ranks. The label always carries the meaning.
        "outline-violet": "border-violet/28 bg-violet/10 text-violet",
        ghost: "[a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        link: "text-primary underline-offset-4 [a&]:hover:underline",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

function Badge({
  className,
  variant = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "span";

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
